// Client voice contracts (BUILD 2 C2-6, C2-7 and C2-8): voice mode, read-aloud
// and spoken confirmation.
//
// Written by WP2-0b and frozen after the b2-contracts tag. WP2-7 implements
// `SpeechPlayer`; WP2-8 the voice controller, `VoicePrefs` and the turn body;
// WP2-9 the voice focus and the confirmation calls. Changes go through WP2-INT
// as a contract change request.
//
// Language detection is automatic. The optional spokenLanguage preference is
// a soft recognition hint, separate from app locale and voice choice. A `lang` below is structural
// provider or model output that steers internals (guards, the TTS voice).

// ── Voice mode ──────────────────────────────────────────────────────────────

export type VoiceStyle = 'handsfree';
/** Deployment platforms with separately recorded Gate B proof. */
export type VoiceDuplexPlatform = 'desktop-chrome' | 'ios-shell' | 'android-shell';
/** What committed an utterance: a tap or the provider's end of turn. */
export type VoiceCommit = 'endpoint' | 'tap';
/**
 * The voice controller's state. 'hearing' shows the live text in the composer
 * row (greyed partials, then finals), never in a separate caption strip.
 */
export type VoicePhase =
  | 'off'
  | 'arming'
  | 'ready'
  | 'hearing'
  | 'committing'
  | 'pending'
  | 'editing'
  | 'sending'
  | 'answering'
  | 'speaking'
  | 'ducked'
  | 'paused';

/** `user_settings.voice_prefs` (PUT /api/v1/speech/voice-preferences takes a partial). */
export type VoicePrefs = {
  enabled: boolean;
  readAloud: boolean;
  speakWith: 'poneva' | 'screen_reader';
  rate: number;
  undoMs: { endpoint: number } | 'always_ask';
  endpointDelayMs: number;
  spokenApprovals: boolean;
  fullDuplex: boolean;
  spokenLanguage: string | null;
};

/** `company_profiles.speech_policy`: the company's switches (owner or admin). */
export type SpeechPolicy = { voiceMode: boolean; readAloud: boolean; spokenApprovals: boolean };

/** `voice` in GET /api/v1/speech/config (C2-6). */
export type VoiceConfig = {
  /** Configured processors that may receive audio or synthesis text. */
  processors: string[];
  available: boolean;
  reason: string | null;
  handsFree: { available: boolean; reason: string | null };
  fullDuplex: {
    available: boolean;
    reason: string | null;
    platforms: VoiceDuplexPlatform[];
  };
  undoMs: { endpoint: number };
  idleExitSec: { handsfree: number };
  policy: SpeechPolicy;
  prefs: VoicePrefs;
  /** Local lane-only timing diagnostics; absent outside local. */
  laneTiming?: boolean;
};

/** `readAloud` in GET /api/v1/speech/config (C2-6). Voices are never listed. */
export type ReadAloudConfig = {
  /** Configured processors that may receive audio or synthesis text. */
  processors: string[];
  available: boolean;
  /** `payment_required` when usage billing refused (C2-5). */
  reason: string | null;
  rate: { min: number; max: number };
};

/**
 * The voice-origin marker (C2-7 item 6): a data part WP2-8 adds to the user
 * message it sends from voice mode, for the thread's display. The model never
 * reads it (the server drops data parts from history) and nothing routes on it.
 */
export const VOICE_ORIGIN_DATA_TYPE = 'data-speech';
export type VoiceOriginData = {
  via: 'voice';
  commit: VoiceCommit;
  autoSent: boolean;
  provider: string;
  interrupted?: VoiceTurnBody['interrupted'];
};

/**
 * The client's one view of voice mode (ui/voice/state.ts, filled by WP2-8):
 * GET /api/v1/speech/config's `voice` and `readAloud` blocks, which read-aloud
 * (WP2-7), the voice controller and its settings (WP2-8) and the cards
 * (WP2-9) all read, so a preference or policy written in one reaches the
 * others. A block is null until loaded, or when the server sends none
 * (unavailable).
 */
import type { SpeechConfig } from '@/speech/contracts';

export type VoiceConfigState = {
  voice: VoiceConfig | null;
  readAloud: ReadAloudConfig | null;
  /** The authenticated, company-scoped speech snapshot. */
  speech: SpeechConfig | null;
};
export interface VoiceConfigStore {
  current(): VoiceConfigState;
  subscribe(cb: () => void): () => void;
  /** Reloads the blocks, after a preference or policy write. */
  refresh(scopeKey?: string): Promise<void>;
}
/** Whether voice mode is on in this tab: the controller sets it; everything else reads it. */
export interface VoiceModeState {
  active(): boolean;
  subscribe(cb: () => void): () => void;
}

/**
 * The chat request's `voice` field (C2-7), sent only for a voice-origin turn.
 * The server drops it on shared and headless turns.
 */
export type VoiceTurnBody = {
  commit: VoiceCommit;
  style: 'handsfree';
  sessionId: string;
  provider: string;
  degraded: boolean;
  autoSent: boolean;
  undoMs: number;
  /** The utterance's `committed.lang` (structural). */
  lang?: string;
  readAloud: boolean;
  /** The user heard only the first `heardChars` characters of the previous reply. */
  interrupted?: { messageId: string; heardChars: number; playback?: PlaybackPosition };
  /** The previous reply's first audio, on the client clock. */
  firstAudioMs?: number;
  capabilities: { control: true; playback: boolean };
};

// ── Read-aloud (POST /api/v1/speech/speak and /read-source) ─────────────────

/**
 * What `/speak` synthesizes. The server never speaks client-supplied text:
 * each source names text the server already holds (C2-6).
 */
export type SpeakSource =
  | { kind: 'message'; threadId: string; messageId: string; streamId?: string; channel?: 'spoken' }
  | {
      kind: 'stored_message';
      threadId: string;
      messageId: string;
      revision: string;
      format?: 'passages';
    }
  | {
      kind: 'confirmation';
      ref: VoiceRef;
      digest: string;
      messageId: string;
      line?: ConfirmationLine;
    };

/**
 * Which confirmation text a `confirmation` source speaks: the summary, or one
 * of its status lines (the only text spoken for the countdown and the outcome).
 * Absent means the summary.
 */
export type ConfirmationLine =
  | 'summary'
  | 'approving'
  | 'rejecting'
  | 'executed'
  | 'failed'
  | 'unknown';

/** Where playback stopped, for the next turn's `interrupted`. */
export type PlaybackPosition = { id: string; playedMs: number };
export type PlaybackStop = {
  messageId: string;
  sentenceIndex: number;
  charOffset: number;
  playback?: PlaybackPosition;
};

/** Playback speed is the only client output preference. Language comes from each text part. */
export type SpeakOptions = { rate: number };

/** Implemented by WP2-7 (ui/voice/playback/player.ts); used by WP2-8 and WP2-9. */
export interface SpeechPlayer {
  /** Streaming text; split into sentences inside. */
  feed(source: SpeakSource, text: string, done: boolean, opts: SpeakOptions): void;
  /** One-shot canonical message or authorized confirmation. */
  speak(source: SpeakSource, text: string, opts: SpeakOptions, signal?: AbortSignal): Promise<void>;
  /** Stops within 150 ms and aborts every fetch. */
  stop(reason: 'user' | 'barge_in' | 'exit' | 'hidden'): PlaybackStop | null;
  /** Replays cached audio with no new characters; false when nothing is cached. */
  replay(messageId: string): boolean;
  duck(db: number): void;
  unduck(): void;
  /** Recent scheduled output RMS, including the device's echo tail. */
  playbackLevel?(): number;
  /** Estimated coherent echo at the microphone; samples stay local. */
  echoLevel?(samples: Int16Array, sampleRate: number, endedAgoMs: number): number;
  state(): 'idle' | 'loading' | 'speaking' | 'paused';
  subscribe(cb: () => void): () => void;
}

// ── Voice tools and cards (C2-7) ────────────────────────────────────────────

/**
 * A card or proposal a voice turn can refer to. A `connection_action` is a
 * Pipedream action and a `custom_mcp_action` a custom MCP call: both are
 * approved with a tap only (R15).
 */
export type VoiceRef = {
  kind: 'action' | 'knowledge_page_proposal' | 'connection_action' | 'custom_mcp_action';
  id: string;
};

/** The `voice_control` tool's result: a client effect, applied once by the sending client. */
export type VoiceControlResult = {
  ok: true;
  action: 'stop' | 'repeat' | 'exit' | 'rate';
  messageId?: string;
  rate?: number;
};

/** The `withdraw_pending_proposal` tool's result: withdrawn, or why not. Never an approval. */
export type WithdrawResult =
  | { status: 'withdrawn'; ref: VoiceRef }
  | { status: 'not_withdrawn'; ref: VoiceRef; reason: 'not_found' | 'not_pending' | 'not_yours' };
/**
 * The pending card that holds auto-send (guard G5): created by the latest
 * assistant message and actionable by this user now. `shownAt` is when the
 * card was shown, on `performance.now()`, the clock of `Utterance.startedAt`.
 */
export type VoiceFocusCard = VoiceRef & {
  digest: string;
  messageId: string;
  actionable: boolean;
  shownAt: number;
};
/** The latest actionable card holds auto-send (G5); approval stays in the shared action path. */
export interface VoiceFocus {
  current(): VoiceFocusCard | null;
  register(card: VoiceFocusCard): () => void;
  subscribe(cb: () => void): () => void;
}

/**
 * One committed voice utterance. Its text leaves only through `toChatText()`,
 * called by the turn body and the composer insert (the no-keyword-routing
 * guard pins the callers); everything else reads the structural fields.
 * `startedAt` is when its capture began, on `performance.now()`.
 */
export interface Utterance {
  readonly sessionId: string;
  readonly turn: number;
  readonly commit: VoiceCommit;
  readonly lang?: string;
  readonly words: number;
  readonly startedAt: number;
  toChatText(): string;
}
